import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { ref } from "vue";

import { useServices } from "@/lib/openapi";

// ── Types ──────────────────────────────────────────────────────────────────

export interface EntitlementStatus {
    /**
     * The server's answer to "may this org use the feature right now".
     *
     * Gate on this, not on `status`: nearly every org has a `free` row, `free`
     * is an active status, and a platform-wide kill-switch leaves that row
     * untouched — so a status-based gate keeps a disabled feature looking
     * enabled until the requests behind it start failing.
     */
    available: boolean;
    /**
     * Why it is unavailable (`globally_disabled`, `no_subscription`,
     * `dependency_not_active`, `cap_exceeded`). Populated for observability even
     * when `available` is true — pre-enforcement an over-cap org is still let
     * through — so never treat a non-null reason as a refusal.
     */
    reason: string | null;
    status: string | null;
    current_usage: number;
    effective_cap: number;
    near_cap_warning: boolean;
    period_start: string | null;
    period_end: string | null;
    plan_name: string | null;
    overage: boolean;
    /** Tier the subscription switches to at `scheduled_change_at`; null if none. */
    scheduled_plan_tier: string | null;
    /** When a scheduled plan change takes effect; null if nothing is pending. */
    scheduled_change_at: string | null;
}

export interface AllEntitlements {
    live_mode?: EntitlementStatus | null;
    compliancy?: EntitlementStatus | null;
    [key: string]: EntitlementStatus | null | undefined;
}

/** Subscription statuses that grant access to a feature. */
export const SUBSCRIPTION_ACTIVE_STATUSES = new Set<string>([
    "free",
    "active",
    "trialing",
    "cancel_at_period_end",
]);

/** One pricing tier for a usage-based feature (from GET /subscription/features). */
export interface FeatureTier {
    tier_id: string;
    cap: number | null;
    price_cents: number | null;
    /** ISO 4217 code read off the live payment-provider price; null if unresolved. */
    currency: string | null;
    has_stripe_price: boolean;
}

/** One purchasable variant for a variant-type feature. */
export interface FeatureVariant {
    variant_id: string;
    label: string;
    price_cents: number;
    trial_days: number;
    has_stripe_price: boolean;
}

/** Full descriptor for a single monetisable feature (from GET /subscription/features). */
export interface FeatureDefinition {
    feature_id: string;
    type: "usage_based" | "toggle" | "variant";
    label: string;
    description: string;
    dependencies: string[];
    tiers: FeatureTier[];
    variants: FeatureVariant[];
    trial_days: number;
    has_stripe_price: boolean;
}

export interface AllFeatures {
    [feature_id: string]: FeatureDefinition;
}

export interface ContactInfo {
    name: string;
    email: string;
    company: string;
}

export interface CheckoutSessionResponse {
    checkout_url: string;
    session_id: string;
}

export interface PortalSessionResponse {
    portal_url: string;
}

export interface CancelSubscriptionResponse {
    feature_key: string;
    status: string;
    period_end: string | null;
    message: string;
}

// ── Store ──────────────────────────────────────────────────────────────────

export const useSubscriptionStore = defineStore("subscription", () => {
    const toast = useToast();

    const entitlements = ref<AllEntitlements | null>(null);
    const features = ref<AllFeatures | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);

    const services = useServices({
        opts: { throwOnError: false },
        requires: { auth: true, organization: true, stek: false },
    });

    // Non-reactive private state for in-flight dedup and stale-response detection.
    // _fetchToken is incremented on invalidate; a response is discarded if its
    // captured token no longer matches, preventing org-switch races.
    let _inFlight: Promise<void> | null = null;
    let _fetchToken = 0;

    /** GET /subscription/entitlements — resolved via Organization header.
     *
     * Concurrent callers during a single fetch share the same in-flight promise
     * (request dedup). Responses from a superseded fetch (after
     * invalidateEntitlements) are silently discarded by the token guard above.
     */
    const fetchEntitlements = async (): Promise<void> => {
        if (_inFlight) return _inFlight; // dedup concurrent callers

        loading.value = true;
        error.value = null;
        const token = ++_fetchToken;

        _inFlight = (async () => {
            try {
                const { data, response } =
                    await services.getEntitlementsSubscriptionEntitlementsGet();
                if (token !== _fetchToken) return; // stale — org switched
                if (!response.ok) {
                    error.value = "Failed to load subscription status.";
                    return;
                }
                entitlements.value =
                    (data as unknown as AllEntitlements) ?? null;
            } catch {
                if (token !== _fetchToken) return;
                error.value = "Failed to load subscription status.";
            } finally {
                if (token === _fetchToken) {
                    loading.value = false;
                    _inFlight = null;
                }
            }
        })();

        return _inFlight;
    };

    /** Clear entitlements and cancel any in-flight fetch (call on org switch). */
    const invalidateEntitlements = (): void => {
        _fetchToken++; // poison any in-flight response
        entitlements.value = null;
        _inFlight = null;
        loading.value = false;
    };

    /**
     * Whether the current Stek is entitled to a feature, based on the loaded
     * entitlements. This is the single source of truth for gating feature UI
     * and behaviour (e.g. the compliancy evidence option). Reads raw
     * subscription status — independent of the backend's ENTITLEMENT_ENFORCEMENT
     * rollout switch.
     */
    const isEntitled = (featureId: string): boolean => {
        const ent = entitlements.value?.[featureId] ?? null;
        if (ent === null) return false;
        // Both conditions, because each catches what the other cannot.
        //
        // `available` is the server's own answer, and it is the only thing that
        // sees the platform-wide kill-switch: that leaves the subscription row
        // untouched, so a status-only gate shows a disabled feature as working.
        //
        // The status still has to be active on top of it. Until
        // ENTITLEMENT_ENFORCEMENT is switched on, the server forces `allowed=true`
        // for *every* billing refusal — so `available` alone would hand a
        // cancelled or past_due organisation its features back, and deploying the
        // paywall would change what those customers see before anyone decided to
        // enforce anything.
        if (ent.available === false) return false;
        return (
            ent.status != null && SUBSCRIPTION_ACTIVE_STATUSES.has(ent.status)
        );
    };

    /** Why a feature is unavailable, or null when it is usable. */
    const unavailableReason = (featureId: string): string | null => {
        const ent = entitlements.value?.[featureId] ?? null;
        if (ent === null || isEntitled(featureId)) return null;
        return ent.reason ?? null;
    };

    /** GET /subscription/features — returns the full feature registry */
    const fetchFeatures = async (): Promise<void> => {
        loading.value = true;
        error.value = null;
        try {
            const { data, response } =
                await services.listFeaturesSubscriptionFeaturesGet();
            if (!response.ok) {
                error.value = "Failed to load subscription features.";
                return;
            }
            features.value = (data as unknown as AllFeatures) ?? null;
        } catch {
            error.value = "Failed to load subscription features.";
        } finally {
            loading.value = false;
        }
    };

    /**
     * POST /stripe/checkout-session
     *
     * Usage-based:  startCheckout("live_mode", { tierId: "starter" })
     * Toggle:       startCheckout("some_toggle")
     * Variant:      startCheckout("dashboard", { variantId: "pro" })
     */
    const startCheckout = async (
        featureId: string,
        opts?: { tierId?: string; variantId?: string; successPath?: string }
    ): Promise<CheckoutSessionResponse | null> => {
        loading.value = true;
        error.value = null;
        try {
            const body: Record<string, string | undefined | null> = {
                feature_id: featureId,
                tier_id: opts?.tierId ?? null,
                variant_id: opts?.variantId ?? null,
                success_path: opts?.successPath ?? null,
            };

            const {
                data,
                error: respError,
                response,
            } = await services.createCheckoutSessionStripeCheckoutSessionPost({
                body: body as Parameters<
                    typeof services.createCheckoutSessionStripeCheckoutSessionPost
                >[0]["body"],
            });
            if (!response.ok) {
                // The 409 for a duplicate subscription points at "Manage billing
                // & invoices"; a generic retry message loses the one instruction
                // that helps. SubscribeModal renders `error` inline.
                const msg =
                    (respError as { detail?: string } | null)?.detail ??
                    "Failed to start checkout. Please try again.";
                error.value = msg;
                toast.add({
                    severity: "error",
                    summary: "Checkout failed",
                    detail: msg,
                    life: 5000,
                });
                return null;
            }
            return (data as CheckoutSessionResponse) ?? null;
        } catch {
            const msg = "Failed to start checkout. Please try again.";
            error.value = msg;
            toast.add({
                severity: "error",
                summary: "Checkout failed",
                detail: msg,
                life: 5000,
            });
            return null;
        } finally {
            loading.value = false;
        }
    };

    /** POST /stripe/portal-session — optionally deep-linked to a feature's subscription */
    const openPortal = async (
        featureId?: string,
        opts?: { returnPath?: string }
    ): Promise<PortalSessionResponse | null> => {
        loading.value = true;
        error.value = null;
        try {
            // Where the portal's "back" link should land. The billing page is
            // org-scoped, so the backend's single STRIPE_PORTAL_RETURN_URL cannot
            // address it — the page we are on can. Only the path is sent; the
            // backend validates it and supplies the origin itself.
            const returnPath =
                opts?.returnPath ??
                window.location.pathname + window.location.search;

            const {
                data,
                error: respError,
                response,
            } = await services.createPortalSessionStripePortalSessionPost({
                body: { feature_key: featureId, return_path: returnPath },
            });
            if (!response.ok) {
                const detail =
                    (respError as { detail?: string } | null)?.detail ??
                    "Failed to open billing portal.";
                error.value = detail;
                toast.add({
                    severity: "error",
                    summary: "Portal unavailable",
                    detail,
                    life: 5000,
                });
                return null;
            }
            return (data as PortalSessionResponse) ?? null;
        } catch {
            const msg = "Failed to open billing portal.";
            error.value = msg;
            toast.add({
                severity: "error",
                summary: "Portal unavailable",
                detail: msg,
                life: 5000,
            });
            return null;
        } finally {
            loading.value = false;
        }
    };

    /** POST /subscription/{feature_key}/cancel */
    const cancelSubscription = async (
        featureKey: string
    ): Promise<CancelSubscriptionResponse | null> => {
        loading.value = true;
        error.value = null;
        try {
            const {
                data,
                error: respError,
                response,
            } = await services.cancelSubscriptionSubscriptionFeatureKeyCancelPost(
                { path: { feature_key: featureKey } }
            );
            if (!response.ok) {
                // Four distinct refusals live behind this call — no subscription,
                // already scheduled, a billed dependent that must go first, and a
                // provider that could not be reached (where retrying IS right).
                // CancelSubscriptionDialog renders `error` verbatim, so collapsing
                // them into "please try again" is what the customer would read.
                error.value =
                    (respError as { detail?: string } | null)?.detail ??
                    "Failed to cancel subscription. Please try again.";
                return null;
            }
            return (data as unknown as CancelSubscriptionResponse) ?? null;
        } catch {
            const msg = "Failed to cancel subscription. Please try again.";
            error.value = msg;
            return null;
        } finally {
            loading.value = false;
        }
    };

    return {
        entitlements,
        features,
        loading,
        error,
        isEntitled,
        unavailableReason,
        fetchEntitlements,
        invalidateEntitlements,
        fetchFeatures,
        startCheckout,
        openPortal,
        cancelSubscription,
    };
});

export const useSubscriptionManager = () => storeToRefs(useSubscriptionStore());
